<script lang="ts">
import { computed, defineComponent, h, unref } from 'vue';

import { classNames } from '@components/utils';

import {
  makeLocaleProps,
  makeStatusProps,
  makeTagProps,
  makeTextProps,
  makeThemeProps,
  makeTitleProps,
  makeVariantProps,
  provideTheme,
  useLocale,
  useProxyModel,
  useStatus,
  useVariant
} from '../../composable';
import { CdsButton, CdsFunctionalButton } from '../CdsButton';
import { CdsIcon } from '../CdsIcon';

import { makeNotificationProps, useNotification } from './composable';

import type { PropType } from 'vue';
import type { ComponentProps } from '@components/utils';

export const componentName = 'CdsNotification';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Состояние уведомления
     */
    modelValue: {
      type: Boolean as PropType<boolean>,
      default: true
    },

    ...makeTitleProps(),
    ...makeTextProps(),
    ...makeNotificationProps(),
    ...makeVariantProps(),
    ...makeStatusProps(),
    ...makeTagProps(),
    ...makeLocaleProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие клика по кнопке закрытия
     * @param event
     */
    'click:close': (event: MouseEvent) => true,
    /**
     * Событие изменения состояния
     * @param value
     */
    'update:modelValue': (value: boolean) => true
  },
  setup(props, { slots, emit }) {
    const { themeClasses } = provideTheme(props);
    const isActive = useProxyModel(props, 'modelValue');
    const { icon } = useNotification(props);
    const { notification: notificationLocale } = useLocale(props);
    const { variantClasses } = useVariant(props, componentName);
    const { statusClasses } = useStatus(props, componentName);

    const closeProps = computed<ComponentProps<typeof CdsButton>>(() => ({
      icon: props.closeIcon,
      'aria-label': notificationLocale.closeLabel,
      onClick(event: MouseEvent) {
        isActive.value = false;
        emit('click:close', event);
      }
    }));

    return () => {
      const hasPrepend = !!(slots.prepend || unref(icon));
      const hasAppend = !!slots.append;
      const hasTitle = !!(slots.title || props.title);
      const hasText = !!(slots.text || props.text || slots.default);
      const hasActions = !!slots.actions;
      const hasClose = !!(slots.close || props.closable);

      return (
        unref(isActive) &&
        h(
          props.tag,
          {
            role: 'alert',
            class: classNames('cds-notification', unref(variantClasses), unref(statusClasses), unref(themeClasses))
          },
          [
            hasPrepend &&
              h(
                'div',
                { key: 'prepend', class: 'cds-notification__prepend' },
                slots.prepend || h(CdsIcon, { name: unref(icon) })
              ),
            h('div', { class: 'cds-notification__content' }, [
              hasTitle && h('div', { key: 'title', class: 'cds-notification__title' }, slots.title?.() ?? props.title),

              hasText &&
                h('span', { key: 'text', class: 'cds-notification__text' }, [
                  slots.text?.() ?? props.text,
                  slots.default?.()
                ]),

              hasActions && h('div', { key: 'actions', class: 'cds-notification__actions' }, slots.actions?.())
            ]),
            hasAppend && h('div', { key: 'append', class: 'cds-notification__append' }, slots.append?.()),
            hasClose &&
              h(
                'div',
                { key: 'close', class: 'cds-notification__close' },
                slots.close
                  ? slots.close?.({ props: unref(closeProps) })
                  : h(CdsFunctionalButton, { ...unref(closeProps) })
              )
          ]
        )
      );
    };
  }
});
</script>

<style lang="scss">
@import './CdsNotification';
</style>
